import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { duplicateOrder } from '../../../services/ordersApi';
import {
  OrderHero,
  OrderItemsTable,
  CustomerInfoCard,
  QuickActions,
  DeliveryInfoCard,
  OrderTimeline,
  OrderNotesBanner,
} from './components';
import {
  OrderDetailHero,
  OrderItem as UiOrderItem,
  OrderTotals,
  CustomerCardInfo,
  DeliveryInfo,
  OrderTimelineStep,
} from './orderDetailsTypes';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { useOrdersStore } from '../../../store/ordersStore';
import { useCustomersStore } from '../../../store/customersStore';
import { backendStatusToUi } from '../Orders/salesRepOrdersTypes';
import { Order, OrderStatusHistoryEntry } from '../../../services/ordersApi';
import { Customer } from '../../../services/customersApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './SalesRepOrderDetails.module.css';

const STATUS_TIMELINE: Array<{ key: string; label: string }> = [
  { key: 'pending', label: 'Created' },
  { key: 'confirmed', label: 'Approved' },
  { key: 'shipped', label: 'In Transit' },
  { key: 'delivered', label: 'Delivered' },
];

function buildHero(o: Order): OrderDetailHero {
  return {
    orderId: formatOrderId(o.id),
    customer: o.customer_name || `Customer #${o.customer_id}`,
    totalValue: `$${Number(o.total_amount).toFixed(2)}`,
    estProfit: `$${(Number(o.total_amount) * 0.18).toFixed(2)}`,
    marginPct: 'Margin —',
    itemCount: `${o.items?.length ?? 0} items`,
    deliveryDate: o.delivered_at ? new Date(o.delivered_at).toLocaleDateString() : 'Pending',
    paymentTerms: 'Credit · 30 Days',
    paymentStatus: o.invoice?.status === 'paid' ? 'Paid' : 'Pending',
    status: backendStatusToUi(o.status),
  };
}

function buildItems(o: Order): UiOrderItem[] {
  return (o.items ?? []).map((it, i) => ({
    id: String(it.id ?? i),
    product: it.product_name || `SKU ${it.sku_id}`,
    sku: it.sku_code || String(it.sku_id),
    category: '—',
    qty: it.quantity,
    unitPrice: `$${Number(it.unit_price).toFixed(2)}`,
    total: `$${(it.line_total ?? it.unit_price * it.quantity).toFixed(2)}`,
  }));
}

function buildTotals(o: Order): OrderTotals {
  return {
    subtotal: `$${Number(o.total_amount).toFixed(2)}`,
    estimatedMargin: `$${(Number(o.total_amount) * 0.18).toFixed(2)}`,
    totalAmount: `$${Number(o.total_amount).toFixed(2)}`,
  };
}

function buildCustomer(c: Customer | null): CustomerCardInfo {
  if (!c) {
    return { name: '—', contact: '—', phone: '—', email: '—', location: '—' };
  }
  return {
    id: c.id,
    name: c.store_name || c.full_name,
    contact: c.full_name,
    phone: c.contact_phone || '—',
    email: c.email,
    location: [c.address_line1, c.city, c.state].filter(Boolean).join(', ') || '—',
  };
}

function buildDelivery(o: Order): DeliveryInfo {
  return {
    expectedDate: o.delivered_at
      ? new Date(o.delivered_at).toLocaleDateString()
      : o.shipped_at
        ? new Date(o.shipped_at).toLocaleDateString()
        : 'TBD',
    deliveryWindow: '—',
    distance: '—',
  };
}

function buildTimeline(o: Order, history: OrderStatusHistoryEntry[]): OrderTimelineStep[] {
  const reachedIndex = STATUS_TIMELINE.findIndex((s) => s.key === o.status);
  return STATUS_TIMELINE.map((step, i) => {
    const matching = history.find((h) => h.to_status === step.key);
    return {
      id: step.key,
      label: step.label,
      meta: matching ? new Date(matching.changed_at).toLocaleString() : 'Pending',
      state: i < reachedIndex ? 'completed' : i === reachedIndex ? 'active' : 'pending',
    };
  });
}

const SalesRepOrderDetails: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const id = params.get('id');
  const [duplicating, setDuplicating] = useState(false);

  const handleDuplicate = async () => {
    if (!id) return;
    if (!window.confirm('Create a new pending order with the same customer and items?')) return;
    setDuplicating(true);
    try {
      const created = await duplicateOrder(id);
      navigate(`/orders/details?id=${created.id}`);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to duplicate order');
    } finally {
      setDuplicating(false);
    }
  };

  const current = useOrdersStore((s) => s.current);
  const history = useOrdersStore((s) => s.history);
  const loading = useOrdersStore((s) => s.loading);
  const error = useOrdersStore((s) => s.error);
  const fetchOne = useOrdersStore((s) => s.fetchOne);
  const fetchHistory = useOrdersStore((s) => s.fetchHistory);

  const customer = useCustomersStore((s) => s.item);
  const fetchCustomer = useCustomersStore((s) => s.fetchOne);

  useEffect(() => {
    if (!id) return;
    fetchOne(id).then((order) => {
      if (order?.customer_id) fetchCustomer(order.customer_id).catch(() => undefined);
    }).catch(() => undefined);
    fetchHistory(id);
  }, [id, fetchOne, fetchHistory, fetchCustomer]);

  const hero = useMemo(() => (current ? buildHero(current) : null), [current]);
  const items = useMemo(() => (current ? buildItems(current) : []), [current]);
  const totals = useMemo(() => (current ? buildTotals(current) : null), [current]);
  const customerInfo = useMemo(() => buildCustomer(customer), [customer]);
  const delivery = useMemo(() => (current ? buildDelivery(current) : null), [current]);
  const timeline = useMemo(() => (current ? buildTimeline(current, history) : []), [current, history]);

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <Link to="/orders" className={styles.backLink}>
          ← Back to Orders
        </Link>
        <div className={styles.topActions}>
          <button
            type="button"
            className={styles.printBtn}
            onClick={() => window.print()}
            disabled={!current}
          >
            🖨 Print
          </button>
          <button
            type="button"
            className={styles.duplicateBtn}
            onClick={handleDuplicate}
            disabled={!current || duplicating}
            title="Create a new pending order with the same customer and items"
          >
            {duplicating ? 'Duplicating…' : '+ Duplicate Order'}
          </button>
        </div>
      </div>

      <AsyncBoundary
        loading={loading && !current}
        error={error}
        empty={!loading && !current}
        emptyMessage={id ? 'Order not found' : 'No order selected'}
        onRetry={id ? () => fetchOne(id) : undefined}
      >
        {hero && totals && delivery && (
          <>
            <OrderHero hero={hero} />
            <div className={styles.body}>
              <div className={styles.left}>
                <OrderItemsTable items={items} totals={totals} />
                <OrderTimeline steps={timeline} />
                <OrderNotesBanner notes="" />
              </div>
              <div className={styles.right}>
                <CustomerInfoCard info={customerInfo} />
                <QuickActions orderId={id ?? undefined} />
                <DeliveryInfoCard info={delivery} />
              </div>
            </div>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
};

export const SalesRepOrderDetailsPage: React.FC = () => {
  return <SalesRepOrderDetails />;
};

export { SalesRepOrderDetails };
export default SalesRepOrderDetails;
